import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Divider, Form, Input, message, Space, Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Logomark from '../components/Logomark';
import { useAuthStore } from '../store/authStore';

const { Text } = Typography;

/** A bejelentkező oldal egyszeri üzenete — a Felhasználók oldal írja (#109). */
export const LOGIN_NOTICE_KEY = 'easyevnts.loginNotice';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isLoading } = useAuthStore();
  const [form] = Form.useForm();
  const { t } = useTranslation();

  // Honnan jöttünk:
  //  1) ProtectedRoute Navigate-eli ide state.from-mal (React Router-ben maradt session).
  //  2) Az API interceptor ?next=... query paramot tesz a hard-redirectre
  //     (amikor a refresh token is lejárt és újrabejelentkezés kell).
  const resolveRedirectTarget = (): string => {
    const fromState = (location.state as { from?: { pathname?: string; search?: string } } | null)
      ?.from;
    if (fromState?.pathname) {
      return `${fromState.pathname}${fromState.search ?? ''}`;
    }
    const params = new URLSearchParams(location.search);
    const next = params.get('next');
    if (next && next.startsWith('/') && !next.startsWith('//')) {
      return next;
    }
    return '/';
  };

  // A Felhasználók oldal ide küld, ha valaki a SAJÁT jelszavát írta át: az új
  // jelszó minden korábbi bejelentkezést lezár, a sajátot is (#109), ezért
  // előbb kijelentkeztetünk, és itt mondjuk meg, miért. Az üzenet a
  // sessionStorage-on át jön, nem a router state-jén: kijelentkezéskor a
  // ProtectedRoute saját `<Navigate state={{ from }}>`-ja felülírja a /login
  // bejegyzés state-jét (mérve élőben). Egyszer olvassuk, és töröljük.
  const [notice] = React.useState<string | null>(() => {
    try {
      const v = sessionStorage.getItem(LOGIN_NOTICE_KEY);
      if (v) sessionStorage.removeItem(LOGIN_NOTICE_KEY);
      return v;
    } catch {
      return null;
    }
  });

  const handleLogin = async (values: any) => {
    try {
      await login(values.email, values.password);
      message.success(t('auth.login_success'));
      navigate(resolveRedirectTarget(), { replace: true });
    } catch (error) {
      // Error is handled in the store
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'var(--paper)',
        backgroundImage:
          'radial-gradient(circle at 18% 12%, rgba(184, 137, 61, 0.08) 0%, transparent 38%), radial-gradient(circle at 88% 78%, rgba(31, 58, 45, 0.07) 0%, transparent 42%), radial-gradient(circle at 50% 110%, rgba(215, 53, 28, 0.06) 0%, transparent 50%)',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: 'var(--paper-light)',
          border: '1px solid rgba(26, 22, 18, 0.1)',
          borderRadius: 14,
          padding: '40px 36px',
          boxShadow: '0 30px 60px -30px rgba(26, 22, 18, 0.25)',
        }}
      >
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div style={{ textAlign: 'center' }}>
            <Logomark size={30} />
            <div
              style={{
                marginTop: 18,
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                color: 'var(--ink-faded)',
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: 'var(--vermilion)',
                  marginRight: 8,
                  verticalAlign: 'middle',
                }}
              />
              {t('auth.login')}
            </div>
          </div>

          {notice === 'password-changed' && (
            <Alert type="info" showIcon message={t('auth.password_changed_sign_in')} />
          )}

          <Form
            form={form}
            name="login"
            onFinish={handleLogin}
            layout="vertical"
            size="large"
            requiredMark={false}
          >
            <Form.Item
              name="email"
              rules={[
                { required: true, message: t('auth.email') },
                { type: 'email', message: t('auth.email') },
              ]}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'var(--ink-faded)' }} />}
                placeholder={t('auth.email')}
              />
            </Form.Item>

            <Form.Item
              name="password"
              rules={[
                { required: true, message: t('auth.password') },
                { min: 6, message: t('auth.password') },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'var(--ink-faded)' }} />}
                placeholder={t('auth.password')}
              />
            </Form.Item>

            <div style={{ textAlign: 'right', marginTop: -12, marginBottom: 20 }}>
              <Link to="/forgot-password" style={{ color: 'var(--ink-soft)', fontSize: 13 }}>
                {t('auth.forgot_password')}
              </Link>
            </div>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" loading={isLoading} block>
                {t('auth.login')}
              </Button>
            </Form.Item>
          </Form>

          <Divider style={{ margin: '4px 0', borderColor: 'rgba(26,22,18,0.1)' }}>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 10,
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--ink-faded)',
              }}
            >
              {t('common.or', 'vagy')}
            </span>
          </Divider>

          <div style={{ textAlign: 'center' }}>
            <Text style={{ color: 'var(--ink-soft)' }}>
              {t('auth.no_account')}{' '}
              <Link to="/register" style={{ color: 'var(--vermilion)', fontWeight: 500 }}>
                {t('auth.register')}
              </Link>
            </Text>
          </div>

          <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 4 }}>
            <LanguageSwitcher />
          </div>
        </Space>
      </div>
    </div>
  );
};

export default Login;
